import axios from 'axios';
import {
    ENV_DEVELOPMENT,
    ENV_PRODUCTION,
    HOST_LOCALHOST,
    HOST_LOOPBACK_IPV4,
    URL_DDB_DEV,
    URL_DDB_DEV_PROXY,
    URL_DDB_PROD,
} from '../config/appConstants';
import { EncryptData } from './rayEncrypt';

// ─── 환경 판별 (제거) ─────────────────────────────────────────────────────────
// IMP-432 (#380) — 종전 `getEnv()`(raymedical.com 도메인만 production) 를 제거했다.
// 호스트에 따라 환경이 갈리면 같은 코드가 접속 주소마다 다른 인프라를 보게 되고, 그 판정을
// DDB 라우팅과 Sentry 가 공유해 한쪽 정책 변경이 다른 쪽으로 새어 나갔다.
// 이제 소비처별로 신호가 분리돼 있다 — 본 파일 `DDB_ENV`(코드 고정) / `DEPLOY_CHANNEL`
// (`config/appConstants.js`, env `REACT_APP_NODE_ENV` 파생) / `config/index.js CheckEnv()`(코드 고정).
// 세 신호의 결정 대상·배선 출처는 `config/appConstants.js` "환경 신호 3계열" 블록이 SSoT.

// ─── DDB 업스트림 환경 고정 ──────────────────────────────────────────────────
// RAYGuard 는 dev DDB 인프라가 없다 — 로컬 개발도 production DB 를 직접 사용한다.
// 따라서 DB upstream 은 호스트 분류와 무관하게 production 으로 고정한다.
//
// IMP-432 (#380) — dev DDB 인프라가 도입되면 되돌릴 지점은 이 상수 1곳이 아니다.
// 아래가 전수이며, 하나라도 빠지면 `DDB_ENV` 만 바꿔도 dev 경로가 복구되지 않는다
// (PR #545 review R2 에서 ⑤⑥⑦ 누락 지적 — 목록 밖 항목은 전제가 조용히 거짓이 되는 시점을 놓친다):
//   ① 본 상수 `DDB_ENV`
//   ② `setupProxy.js` UPSTREAM_URL (server-side RSA 대행 + CORS 우회 프록시의 forward 대상)
//   ③ 아래 `isDev` debug sidecar (현재 항상 false — ②와 짝)
//   ④ 정책 서술 — `config/appConstants.js` 의 "환경 신호 3계열" 블록(신호 3개의 결정 대상·배선
//      출처 SSoT) + 같은 파일 URL_DDB_* 블록 + `config/index.js` CheckEnv 블록
//      + `setupProxy.js` 헤더(현재는 본 목록을 가리키는 포인터. 목록 복제 금지 — review R6 D4)
//   ⑤ 본 파일 insecure-context fail-closed 가드 — URL 결정보다 **먼저** 차단하므로, 이걸 그대로
//      두면 `DDB_ENV=dev` 로 되돌려도 LAN/IP(http) 개발과 ②의 RSA 보강 분기가 복구되지 않는다
//   ⑥ 회귀 가드 테스트 — 특정 단언 문구가 아니라 **케이스의 목적**을 지시한다(이 목록은 본 PR 안에서
//      두 번 드리프트했다: 수치가 낡았고, 지시 대상이 삭제됐다):
//        · `ddbRouting.test.js` — 호스트 전수에 대해 prod URL 로 고정되는 케이스
//        · `sentry.test.js` — hostname 을 바꿔도 `environment` 가 배포 채널만 따르는 동작 케이스
//          (호스트 분기 재도입 차단. 종전의 텍스트 금지 단언은 R4 에서 동작 케이스로 대체됐다)
//   ⑦ "dev/prod DDB 엔드포인트가 동일 서버" 전제 (`config/index.js` 주석) — 만료 조건이 정확히
//      "dev DDB 신설" 이라 본 목록과 트리거를 공유한다
const DDB_ENV = ENV_PRODUCTION;

// ─── setupProxy 가용 환경 가드 ──────────────────────────────────────────────
// `/proxy/calldynamo-dev` 상대 경로는 src/setupProxy.js 가 떠있는 환경에서만 작동.
// production FE 빌드에는 setupProxy.js 가 포함되지 않으므로, production 도메인
// (예: rayguard.rayteams.com) 이 dev 분류돼도 절대 proxy 경로를 쓰면 안 된다.
//
// 매칭 대상: 로컬 dev 서버(craco start) 가 떠있는 환경.
//   - localhost / 127.0.0.1: CRA 기본 dev hostname
//   - 사설망 IPv4 (10.x / 172.16-31.x / 192.168.x): LAN dev 서버 접속
//   - 그 외 IPv4 (예: 23.50.60.77 같은 사내 고정 IP) 도 IP 패턴으로 식별
//
// 도메인 형태 host 는 항상 false → absolute URL 직접 호출.
const ANY_IPV4_REGEX = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
export const isLocalDevServer = (host) => {
    if (typeof host !== 'string' || host.length === 0) return false;
    if (host === HOST_LOCALHOST || host === HOST_LOOPBACK_IPV4) return true;
    return ANY_IPV4_REGEX.test(host);
};

// ─── DDB upstream URL 라우팅 룩업 ───────────────────────────────────────────
// 라우팅 정책 SSoT. callDynamoV2 와 단위 테스트가 동일 함수 사용.
//   ① env=dev + isLocalDevServer host → URL_DDB_DEV_PROXY (setupProxy 경유, CORS 우회)
//   ② env=dev + 그 외 host          → URL_DDB_DEV       (절대 URL 직접 호출)
//   ③ env=prod                      → URL_DDB_PROD      (절대 URL 직접 호출)
export const resolveDdbApiUrl = ({ env, host }) => {
    if (env === ENV_DEVELOPMENT && isLocalDevServer(host)) return URL_DDB_DEV_PROXY;
    if (env === ENV_DEVELOPMENT) return URL_DDB_DEV;
    return URL_DDB_PROD;
};

// ─── 진단 로그 가드 ──────────────────────────────────────────────────────────
// dev 호스트(setupProxy 가용 환경)에서만 verbose stage 로그를 남긴다.
// production / dev 도메인 환경은 매 호출마다 console 출력 노이즈 차단.
const isVerboseLogHost = () => {
    const host = (typeof location !== 'undefined' && location.hostname) || '';
    return isLocalDevServer(host);
};
const dlog = (...args) => { if (isVerboseLogHost()) console.log(...args); };

// BUG-092 — write 실패는 호출자에게 전파.
// 기존: 모든 에러 catch → `[]` 반환. write 호출자가 "성공" 으로 인식 → false-positive "Saved" UI.
// 변경: read 타입(query/scan/get/batchGet) 만 graceful `[]` fallback 유지. write 타입은 throw.
const READ_TYPES = new Set(['query', 'scan', 'get', 'batchGet']);

// gateway 가 HTTP 200 으로 반환하면서 응답 본문에 에러를 담는 silent-success 케이스 탐지.
// `await` 가 throw 안 했다고 write 가 실제 성공한 것은 아니므로, 본문 시그널을 검사한다.
//
// IMP-130 (PR #191 follow-up): 본래 smartDentQueries.js 에 인라인 정의되어 putAllocationAudit
// 한 곳에서만 사용. 위치를 ddb.js 로 이전 + export 하여 향후 write 호출자가 공유할 수 있게 한다.
//   - 보류(AC-1): 아래 7-시그널 휴리스틱은 false-positive 위험(예: { message:'ok', code:200 })이
//     있으나, BE DynamoV2 응답 schema 협의로 단일 에러 필드가 확정되기 전까지 축소하지 않는다.
//   - 보류(AC-2): DoDB('update'|'put') 내부 자동 호출(전 write 자동 보호)도 AC-1 확정 후 진행.
//     섣부른 통합은 false-positive 를 전 write 경로로 확산시키므로 현 단계에서는 적용하지 않는다.
export const isWriteResponseError = (response) => {
    if (response === null || response === undefined) return false;
    if (typeof response !== 'object') return false;
    // 알려진 에러 시그널 — gateway/Lambda 가 사용할 수 있는 패턴들
    if (response.error) return true;                       // { error: ... }
    if (response.errorMessage) return true;                // AWS Lambda invoke error
    if (response.errorType) return true;                   // AWS Lambda
    if (response.success === false) return true;           // { success: false }
    if (response.statusCode && response.statusCode >= 400) return true;  // wrapped HTTP
    if (response.__type) return true;                      // DDB error type field
    if (response.message && response.code) return true;    // AWS SDK error shape
    return false;
};

export const DoDB = async (type, data, region = "ap-northeast-2") => {
    const payload = {
        data: {
            type,
            region,
            params: data
        }
    };

    return callDynamoV2(payload);
};

async function callDynamoV2(originPayload) {
    const host = (typeof location !== 'undefined' && location.hostname) || '';
    const origin = (typeof location !== 'undefined' && location.origin) || '';
    const env = DDB_ENV;   // 호스트 무관 prod 고정 (dev DDB 인프라 부재)
    const isDev = env === ENV_DEVELOPMENT;
    const apiUrl = resolveDdbApiUrl({ env, host });
    const type = originPayload?.data?.type;
    const tableName = originPayload?.data?.params?.TableName;

    // 진단용 — 요청 송출 직전 컨텍스트 가시화. setupProxy 로딩/dev 분기/URL 등 즉시 식별.
    dlog('[callDynamoV2 →]', {
        host, origin, env, isDev,
        isLocalDevServer: isLocalDevServer(host),
        apiUrl, op: type, tableName,
    });

    // Web Crypto API(`window.crypto.subtle`) 가용 여부 — RSA 암호화 가능성 판정.
    // 브라우저는 secure context(https / localhost / 127.0.0.1) 에서만 subtle 를 노출.
    // 내부망 IP(예: http://23.50.60.77:3000) 는 insecure context → subtle 미정의.
    //
    // IMP-432 (#380) — 업스트림이 prod 고정이 되면서 setupProxy 경유가 사라졌다. 종전에는
    // 암호화를 skip 하고 평문 `debug` 만 보내면 dev 서버가 server-side RSA 로 `data` 를
    // 보강했지만, 이제 그 보강 주체가 없어 암호화 없이 보내면 빈 본문이 업스트림에 도달한다.
    // 무음 fail-open 금지(CLAUDE.md §7) — 관측 가능한 에러로 fail-closed 한다.
    const cryptoSubtleAvailable = typeof window !== 'undefined'
        && window.crypto && window.crypto.subtle;
    if (!cryptoSubtleAvailable) {
        console.error('[guard] callDynamoV2: insecure context — 요청 차단', {
            handler: 'callDynamoV2', host, origin, op: type, tableName, apiUrl,
            reason: 'window.crypto.subtle 미가용 — https 접속 필요 (yarn s:https)',
        });
        throw new Error('DDB request blocked: Web Crypto unavailable (insecure context)');
    }

    try {
        const payload = await EncryptData(originPayload.data);
        // dev API 의 디버깅 sidecar. DDB_ENV=prod 고정으로 현재 항상 false —
        // dev DDB 인프라 복구 시 DDB_ENV / setupProxy UPSTREAM_URL 과 함께 되살릴 지점.
        if (isDev) {
            payload.debug = originPayload;
        }
        dlog('[callDynamoV2 send]', {
            apiUrl,
            payloadKeys: Object.keys(payload || {}),
            cryptoSubtleAvailable: !!cryptoSubtleAvailable,
            hasDebug: !!payload?.debug,
        });

        const ret = await axios.post(apiUrl, payload);
        dlog('[callDynamoV2 ←]', {
            apiUrl, op: type,
            httpStatus: ret?.status,
            dataKind: Array.isArray(ret?.data) ? `Array(${ret.data.length})` : typeof ret?.data,
        });
        return ret?.data;
    } catch (error) {
        // 진단용 — gateway 응답 핵심 필드 명시 추출 (axios error 구조 대응).
        // 객체를 console.error 단일 인자로 넘기면 브라우저 캡처 시 'Object' 로 축약될 수 있어
        // 핵심 필드를 message 문자열로 직렬화 (verbose 가드와 무관하게 항상 출력).
        const httpStatus = error?.response?.status;
        const httpStatusText = error?.response?.statusText;
        const errorCode = error?.code;
        const errMsg = error?.message;
        const respBody = error?.response?.data;
        console.error(
            `ERROR[callDynamoV2] op=${type} url=${apiUrl} status=${httpStatus} statusText=${httpStatusText} code=${errorCode} msg=${errMsg}`,
            { op: type, apiUrl, tableName,
                sk: originPayload?.data?.params?.Item?.sk
                    || originPayload?.data?.params?.Key?.sk,
                httpStatus, httpStatusText, errorCode, errorMessage: errMsg, responseBody: respBody,
            },
        );
        if (READ_TYPES.has(type)) {
            return [];
        }
        throw error;
    }
}
